import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slide,
  Typography,
  Box,
  Divider,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { IconInProgress } from "Icons";
import {
  styled,
  keyframes,
  ThemeProvider,
  createTheme,
} from "@mui/material/styles";
import PropTypes from "prop-types";

const theme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
    allVariants: {
      fontFamily: "Poppins, sans-serif",
    },
  },
});

// Slide Transition from Bottom
const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Fade-in and Scale Animation
const fadeInScale = keyframes`
    from {
        opacity: 0;
        transform: scale(0.9);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "8px",
    animation: `${fadeInScale} 300ms cubic-bezier(0.4, 0, 0.2, 1)`,
    transformOrigin: "center center",
    boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
  },
}));

const AnimatedCloseButton = styled(IconButton)(({ theme }) => ({
  transition: "all 250ms ease-in-out",
  color: "#1e293b",
  maxWidth: "40px",
  maxHeight: "40px",
  "&:hover": {
    backgroundColor: "rgba(255,99,71,0.1)",
    transform: "rotate(90deg)",
    color: "#ee3349",
  },
  "&:active": {
    transform: "scale(0.9)",
  },
}));

const DashedDivider = styled("div")(({ theme }) => ({
  width: "1px",
  height: "100%",
  background: "linear-gradient(to bottom, black 50%, transparent 50%)",
  backgroundSize: "1px 10px", // Adjust the 8px value to change dash length
  opacity: 0.9,
}));

const DisplayDetailsModal = ({ isOpen, onClose, formData }) => {
  const [date, timeWithMs] = formData.createdOn.split("T");
  let [hours, minutes] = timeWithMs.split(":");
  hours = parseInt(hours);

  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  const time = `${hours}:${minutes} ${period}`;

  const getReadableTicketType = (type) => {
    switch (type) {
      case "general_feedback":
        return "General Feedback";
      case "content_concern":
        return "Content Concern";
      case "suggestions":
        return "Suggestion";
      default:
        return "Unknown Type";
    }
  };
  return (
    <ThemeProvider theme={theme}>
      <StyledDialog
        open={isOpen}
        onClose={onClose}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            color: "black",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", flex: 1, textAlign: "center" }}
          >
            Your {getReadableTicketType(formData.ticketType)} Has Been Received
          </Typography>
          <AnimatedCloseButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </AnimatedCloseButton>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            {/* Left Column (3/5 width) */}
            <Grid
              size={{ xs: 6, md: 4 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mb={2}
              >
                <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                  {getReadableTicketType(formData.ticketType)}
                </Typography>
                <Typography variant="body1" fontWeight={700} sx={{ mb: 3 }}>
                  #ILETK00{formData.ticketId}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" justifyContent="center">
                <IconInProgress />
                <Typography variant="body1" sx={{ ml: 1, color:'#90BD3F' }}>
                  {" "}
                  In Progress
                </Typography>
              </Box>
            </Grid>

            {/* Dashed Divider */}
            <Grid
              xs={12}
              md={0.2}
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <DashedDivider />
            </Grid>

            {/* Right Column (2/5 width) */}
            <Grid
              size={{ xs: 6, md: 7 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body1" sx={{ mb: 3 }}>
                {formData.description || "No description provided."}
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                // justifyContent="space-between"
              >
                <Typography variant="body1">Created at {date}</Typography>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 1, color: "black" }}
                />
                <Typography variant="body1"> {time}</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          {/* <Button onClick={onClose} color="primary">
          Close
        </Button> */}
        </DialogActions>
      </StyledDialog>
    </ThemeProvider>
  );
};

DisplayDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    description: PropTypes.string,
    screenshot: PropTypes.instanceOf(File),
    attachment: PropTypes.instanceOf(File),
  }).isRequired,
};

export default DisplayDetailsModal;
